import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { api } from "@/lib/api";
import { useLocale } from "@/lib/locale";

export default function ReportAbuse() {
    const { t } = useLocale();
    const TYPES = [
        { id: "impersonation", label: t("report.t_imperson") },
        { id: "minor", label: t("report.t_minor") },
        { id: "deceased_celebrity", label: t("report.t_celeb") },
        { id: "harmful", label: t("report.t_harm") },
        { id: "copyright", label: t("report.t_copy") },
        { id: "other", label: t("report.t_other") },
    ];

    const [type, setType] = useState("other");
    const [details, setDetails] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sentId, setSentId] = useState(null);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!details.trim()) { setErr(t("report.empty")); return; }
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/legal/report", { type, details, email });
            setSentId(data.report_id);
        } catch (e) {
            setErr(t("report.err"));
        } finally {
            setBusy(false);
        }
    };

    if (sentId) {
        return (
            <div className="grain min-h-screen" data-testid="report-sent">
                <Header minimal />
                <main className="max-w-2xl mx-auto px-6 md:px-12 pt-24 pb-32 text-center animate-fade-in">
                    <BrandMark size={40} className="mx-auto mb-8" />
                    <h1 className="h-serif text-4xl md:text-5xl mb-6">{t("report.sent_title")}</h1>
                    <p className="text-ink-textMid text-lg leading-[1.7] mb-10">{t("report.sent_body")}</p>
                    <p className="text-xs text-ink-textLow tracking-widest uppercase">{t("report.ref")} · {sentId}</p>
                    <Link to="/" className="btn-primary inline-block mt-10">{t("report.home")}</Link>
                </main>
            </div>
        );
    }

    return (
        <div className="grain min-h-screen" data-testid="report-abuse-page">
            <Header minimal />
            <main className="max-w-2xl mx-auto px-6 md:px-12 pt-16 pb-32 animate-fade-in">
                <div className="flex items-center gap-4 mb-8">
                    <BrandMark size={36} />
                    <p className="label-eyebrow">{t("report.eyebrow")}</p>
                </div>
                <h1 className="h-serif text-4xl md:text-5xl leading-[1.1] mb-6">
                    {t("report.title_a")} <span className="italic text-ink-gold">{t("report.title_i")}</span>
                </h1>
                <p className="text-ink-textMid text-lg leading-[1.7] mb-12 max-w-xl">{t("report.body")}</p>

                <form onSubmit={submit} className="space-y-8" data-testid="report-form">
                    <div>
                        <label className="label-eyebrow block mb-3">{t("report.type")}</label>
                        <div className="space-y-2">
                            {TYPES.map((tp) => (
                                <label
                                    key={tp.id}
                                    className={`flex items-start gap-3 cursor-pointer p-3 rounded-sm border transition-colors ${
                                        type === tp.id ? "border-ink-gold bg-ink-surface/40" : "border-ink-borderSubtle hover:border-ink-border"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="report-type"
                                        value={tp.id}
                                        checked={type === tp.id}
                                        onChange={() => setType(tp.id)}
                                        className="mt-1.5 accent-ink-gold"
                                        data-testid={`report-type-${tp.id}`}
                                    />
                                    <span className="text-ink-textHigh leading-relaxed">{tp.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="label-eyebrow block mb-3" htmlFor="report-details">{t("report.details")}</label>
                        <textarea
                            id="report-details"
                            data-testid="report-details"
                            rows={6}
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            placeholder={t("report.details_ph")}
                            className="textarea-paper"
                            maxLength={4000}
                        />
                    </div>

                    <div>
                        <label className="label-eyebrow block mb-3" htmlFor="report-email">{t("report.email")}</label>
                        <input
                            id="report-email"
                            data-testid="report-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("report.email_ph")}
                            className="input-line"
                            maxLength={200}
                        />
                    </div>

                    {err && <p className="text-ink-danger text-sm font-serif italic" data-testid="report-error">{err}</p>}

                    <button disabled={busy} type="submit" data-testid="report-submit" className="btn-primary">
                        {busy ? t("report.sending") : t("report.submit")}
                    </button>
                </form>

                <p className="text-xs text-ink-textLow mt-12 leading-relaxed">
                    {t("report.urgent")}{" "}
                    <a href="mailto:os.otherside.app@gmail.com" className="text-ink-gold underline underline-offset-4">os.otherside.app@gmail.com</a>.
                </p>
            </main>
        </div>
    );
}
